import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Truck, Boxes, Ship, Plane, Globe, Shield, Zap, Users, Award, MapPin, Clock, TrendingUp } from 'lucide-react';
import { NavLink } from 'react-router-dom';

export default function Home() {
  return (
    <>
      <Helmet>
        <title>CROSSDOCS — Premium Logistics & Cross‑Docking Solutions</title>
        <meta name="description" content="CROSSDOCS delivers cross‑docking, freight, and warehousing with real‑time visibility, guaranteed SLAs, and a nationwide carrier network." />
      </Helmet>

             {/* Hero Section */}
       <section className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 transform-gpu">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          style={{
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden'
          }}
        >
          <source src="assets/herovideo-3mb.mp4" type="video/mp4" />
        </video>
        
        <div className="container-consistent relative z-20 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
                         <div className="max-w-7xl mx-auto mb-6">
               <motion.h1 
                 initial={{ opacity: 0, x: -100 }}
                 animate={{ opacity: 1, x: 0 }}
                 transition={{ duration: 0.8, ease: "easeOut" }}
                 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-2"
               >
                 Revolutionizing Global
               </motion.h1>
               <motion.h1 
                 initial={{ opacity: 0, x: 100 }}
                 animate={{ opacity: 1, x: 0 }}
                 transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-primary-400"
               >
                 Supply Chain Solutions
              </motion.h1>
             </div>
             <p className="text-base md:text-lg text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
               CROSSDOCS delivers cross‑docking, freight, and warehousing with real‑time visibility, 
               guaranteed SLAs, and a nationwide carrier network that never sleeps.
             </p>
             <div className="flex flex-row items-center justify-center gap-4 mb-8">
                             <NavLink 
                 to="/contact" 
                 className="group inline-flex items-center rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 hover:shadow-xl hover:scale-105 transition-all duration-300"
               >
                 Get a Quote
                 <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </NavLink>
               <NavLink 
                 to="/services" 
                 className="inline-flex items-center rounded-xl px-6 py-3 text-sm font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
               >
                 Explore Services
                </NavLink>
              </div>
            
                                      {/* Stats */}
             {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
               {[
                 { number: '99.95%', label: 'On-time Rate' },
                 { number: '24/7', label: 'Operations' },
                 { number: '1300+', label: 'Carrier Partners' },
                 { number: '48', label: 'States Covered' },
               ].map((stat, index) => (
                 <motion.div
                   key={stat.label}
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                   className="text-center"
                 >
                   <div className="text-xl md:text-2xl font-bold text-primary-400 mb-1">{stat.number}</div>
                   <div className="text-xs text-gray-300">{stat.label}</div>
                 </motion.div>
               ))}
             </div> */}
          </motion.div>
        </div>
        

             </section>

       {/* Tracking Form Section */}
       <section className="w-full bg-gradient-to-r from-slate-50 via-white to-slate-50 pt-4 pb-8 -mt-8 transform-gpu">
         <div className="container-consistent">
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6 }}
             viewport={{ once: true }}
             className="max-w-3xl mx-auto"
           >
             <form 
               className="group relative"
               action="https://tracking.crosdocs.com"
               method="GET"
               target="_blank"
               rel="noopener noreferrer"
             >
               <div className="flex items-center bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200/50 overflow-hidden">
                 <div className="flex-1 relative">
                   <input
                     type="text"
                     name="code"
                     placeholder="Tracking Code"
                     required
                     className="w-full px-6 py-4 text-slate-900 placeholder-slate-400 bg-transparent border-none outline-none text-lg font-medium"
                   />
                   <div className="absolute inset-0 bg-gradient-to-r from-primary-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                 </div>
                 <div className="h-8 w-px bg-gradient-to-b from-transparent via-slate-300 to-transparent"></div>
                 <button
                   type="submit"
                   className="px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300 flex items-center justify-center gap-3 group/btn"
                 >
                   <svg className="w-5 h-5 group-hover/btn:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                   </svg>
                   <span className="group-hover/btn:translate-x-0.5 transition-transform duration-200">Track</span>
                 </button>
               </div>
             </form>
           </motion.div>
         </div>
       </section>

       {/* About Us Section */}
      <section className="pt-0 pb-20 bg-white">
        <div className="container-consistent">
          <div className="grid gap-16 lg:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Revolutionizing Logistics Through Innovation
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Founded in 2018, CROSSDOCS has transformed from a startup vision into a nationwide 
                logistics powerhouse. We combine cutting-edge technology with strategic infrastructure 
                to deliver solutions that exceed expectations.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Our mission is simple: eliminate inefficiencies in logistics by providing intelligent 
                CROSSDOCS solutions that reduce costs, improve speed, and enhance visibility 
                across the entire supply chain.
              </p>
              <NavLink 
                to="/about" 
                className="inline-flex items-center rounded-xl bg-primary-600 px-6 py-3 text-base font-semibold text-white shadow-lg hover:bg-primary-700 hover:shadow-xl transition-all duration-300"
              >
                Learn More About Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </NavLink>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="assets/warehouse-8921538_1280.jpg" 
                  alt="Modern Warehouse Operations"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-xl font-bold mb-2">State-of-the-Art Facilities</h3>
                  <p className="text-base">Advanced automation and real-time tracking systems</p>
                </div>
              </div>
              
              {/* Floating stats */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-lg">
                <div className="text-2xl font-bold text-primary-600">6+</div>
                <div className="text-sm text-slate-600">Years</div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg">
                <div className="text-2xl font-bold text-primary-600">500+</div>
                <div className="text-sm text-slate-600">Customers</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Comprehensive Logistics Solutions
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From CROSSDOCS to last-mile delivery, we provide end-to-end logistics services 
              that keep your supply chain moving efficiently.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Boxes, title: 'CROSSDOCS', desc: 'Inbound to outbound in under 6 hours', image: 'assets/warehouse-8921538_1280.jpg' },
              { icon: Truck, title: 'Line-Haul', desc: 'Regional and long-haul with strict SLAs', image: 'assets/truck-1910871_1280.jpg' },
              { icon: Ship, title: 'Ocean & Drayage', desc: 'Port pickup and container optimization', image: 'assets/ship-105596_1280.jpg' },
              { icon: Plane, title: 'Air & Expedited', desc: 'Next-flight-out priority handling', image: 'assets/airplane-4974678_1280.jpg' },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <service.icon className="h-6 w-6 text-primary-400 mb-2" />
                  <h3 className="text-lg font-bold mb-1">{service.title}</h3>
                  <p className="text-sm text-gray-200">{service.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <div className="grid gap-16 lg:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why Companies Choose CROSSDOCS
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We combine cutting-edge technology, strategic infrastructure, and proven expertise 
                to deliver logistics solutions that exceed expectations.
              </p>
              
              <div className="space-y-6">
                {[
                  { icon: Globe, title: 'Global Network', desc: 'Strategic hubs across major markets with 48-state coverage' },
                  { icon: Shield, title: 'Risk Management', desc: 'Comprehensive insurance and proactive claims handling' },
                  { icon: Zap, title: 'Speed & Efficiency', desc: 'Average 6-hour CROSSDOCS turnaround time' },
                  { icon: Users, title: 'Expert Team', desc: 'Dedicated logistics specialists available 24/7' },
                ].map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <feature.icon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-slate-900 mb-1">{feature.title}</h3>
                      <p className="text-sm text-slate-600">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="assets/port-6670684_1280.jpg" 
                  alt="Port Operations"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-lg font-bold mb-2">Strategic Port Operations</h3>
                  <p className="text-sm">Optimized drayage and container handling at major US ports</p>
                </div>
              </div>
              
              {/* Floating stats */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-lg">
                <div className="text-2xl font-bold text-primary-600">15+</div>
                <div className="text-sm text-slate-600">Port Locations</div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg">
                <div className="text-2xl font-bold text-primary-600">2hr</div>
                <div className="text-sm text-slate-600">Avg. Dwell Time</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Network & Infrastructure */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Nationwide Network Infrastructure
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Strategic CROSSDOCS hubs positioned near major ports, airports, and population centers 
              to minimize transit times and maximize efficiency.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { city: 'Los Angeles', region: 'West Coast Hub', image: 'assets/container-4197259_1280.jpg', features: ['Port of LA/LB', 'Air Cargo', 'CROSSDOCS'] },
              { city: 'Chicago', region: 'Midwest Hub', image: 'assets/warehouse-9006780_1280.jpg', features: ['Rail Terminal', 'Trucking', 'Distribution'] },
              { city: 'Atlanta', region: 'Southeast Hub', image: 'assets/factory-1137994_1280.jpg', features: ['Airport Access', 'Highway Hub', 'Storage'] },
              { city: 'Dallas', region: 'South Central', image: 'assets/mini-storage-2449803_1280.jpg', features: ['Intermodal', 'CROSSDOCS', 'Fulfillment'] },
            ].map((hub, index) => (
              <motion.div
                key={hub.city}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={hub.image} 
                    alt={`${hub.city} Hub`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold mb-1">{hub.city}</h3>
                  <p className="text-primary-400 text-sm mb-3">{hub.region}</p>
                  <ul className="space-y-2">
                    {hub.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-gray-300">
                        <CheckCircle2 className="h-4 w-4 text-primary-400" />
                        {feature}
                  </li>
                ))}
              </ul>
            </div>
              </motion.div>
            ))}
            </div>
          
          {/* Network stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '25+', label: 'CROSSDOCS Hubs' },
              { number: '1300+', label: 'Carrier Partners' },
              { number: '48', label: 'States Covered' },
              { number: '99.95%', label: 'Service Reliability' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary-400 mb-2">{stat.number}</div>
                <div className="text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Meet Our Leadership Team
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Experienced professionals with deep expertise in logistics, technology, and operations, 
              dedicated to delivering exceptional results for our customers.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Chen',
                title: 'CEO & Founder',
                image: 'assets/man-5710164_1280.jpg',
                bio: 'Former VP of Operations at FedEx, 15+ years in logistics',
                expertise: ['Supply Chain Strategy', 'Operations Management', 'Business Development']
              },
              {
                name: 'Michael Rodriguez',
                title: 'CTO',
                image: 'assets/laptop-3196481_1280.jpg',
                bio: 'Ex-Google engineer, built scalable logistics platforms',
                expertise: ['Technology Architecture', 'AI/ML Integration', 'System Optimization']
              },
              {
                name: 'Jennifer Park',
                title: 'VP of Operations',
                image: 'assets/student-849824_1280.jpg',
                bio: 'Former Amazon logistics director, expert in warehouse optimization',
                expertise: ['Process Improvement', 'Quality Control', 'Team Leadership']
              }
            ].map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="aspect-[4/3] overflow-hidden rounded-t-xl">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{member.name}</h3>
                  <p className="text-primary-600 font-semibold mb-3">{member.title}</p>
                  <p className="text-sm text-slate-600 mb-4">{member.bio}</p>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2 text-sm">Expertise:</h4>
                    <ul className="space-y-1">
                      {member.expertise.map((skill) => (
                        <li key={skill} className="flex items-center gap-2 text-xs text-slate-600">
                          <CheckCircle2 className="h-3 w-3 text-primary-500" />
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Trusted by Industry Leaders
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From Fortune 500 companies to innovative startups, businesses across industries 
              rely on CROSSDOCS for their critical logistics needs.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                company: 'TechFlow Solutions',
                logo: 'Apex',
                testimonial: 'CROSSDOCS transformed our supply chain. Their CROSSDOCS reduced our transit time by 40% and improved our customer satisfaction significantly.',
                person: 'Sarah Chen',
                title: 'VP of Operations',
                image: 'assets/laptop-3196481_1280.jpg'
              },
              {
                company: 'Global Manufacturing Co.',
                logo: 'Solvia',
                testimonial: 'The reliability and transparency we get from CROSSDOCS is unmatched. Real-time tracking and proactive communication make all the difference.',
                person: 'Michael Rodriguez',
                title: 'Supply Chain Director',
                image: 'assets/factory-1137994_1280.jpg'
              },
              {
                company: 'E-commerce Express',
                logo: 'Nordik',
                testimonial: 'As we scaled from 100 to 10,000 orders per day, CROSSDOCS scaled with us seamlessly. Their infrastructure is truly enterprise-grade.',
                person: 'Jennifer Park',
                title: 'CEO',
                image: 'assets/warehouse-8921538_1280.jpg'
              }
            ].map((testimonial, index) => (
              <motion.div
                key={testimonial.company}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute -top-3 left-6 bg-primary-600 text-white px-3 py-1 rounded-lg text-sm font-semibold">
                  {testimonial.logo}
                  </div>
                <div className="aspect-[4/3] overflow-hidden rounded-lg mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.company}
                    className="w-full h-full object-cover"
                  />
            </div>
                <blockquote className="text-slate-600 mb-4 italic text-sm">
                  "{testimonial.testimonial}"
                </blockquote>
                <div className="border-t border-slate-200 pt-3">
                  <div className="font-semibold text-slate-900 text-sm">{testimonial.person}</div>
                  <div className="text-xs text-slate-500">{testimonial.title}</div>
                  <div className="text-primary-600 font-medium text-sm">{testimonial.company}</div>
                  </div>
              </motion.div>
                ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </>
  );
}

function CTA() {
  return (
    <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
      <div className="container-consistent text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Logistics?
          </h2>
          <p className="text-lg text-primary-100 max-w-3xl mx-auto mb-8">
            Join hundreds of companies already experiencing the CROSSDOCS difference. 
            Get a guaranteed quote within 24 hours and see how we can optimize your supply chain.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <NavLink 
              to="/contact" 
              className="group inline-flex items-center rounded-xl bg-white px-8 py-4 text-base font-semibold text-primary-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Get Your Quote
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </NavLink>
            <NavLink 
              to="/services" 
              className="inline-flex items-center rounded-xl px-8 py-4 text-base font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
            >
              Explore Services
            </NavLink>
          </div>
          <div className="mt-8 text-primary-100">
            <p className="text-sm">✓ No setup fees • ✓ 24/7 support • ✓ Guaranteed SLAs</p>
        </div>
        </motion.div>
      </div>
    </section>
  );
}






